/**
 * Guided constructor for one NEW keyword handoff rule (escalation-admin-UI
 * spec 2026-07-10 §3.5): overlay `escalation.triggers.<id>` with
 * `when: topic_denylist` fixed — arbitrary-predicate rules stay API-only.
 * NOT Advanced-gated (spec decision 7). Client-side checks are
 * convenience only; the server's validate pass is the gate.
 */
import { useState } from 'react'
import { TagInput } from '@/components/ui/TagInput'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { deriveKey, KEY_RE } from './deriveKey'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'

const RULE_KINDS = [
  { value: 'quote_request', label: 'Quote request' },
  { value: 'customer_request', label: 'Customer asked for a person' },
  { value: 'custom', label: 'Custom…' },
] as const

const inputCls =
  'rounded-sm border border-border bg-background p-1 text-xs text-foreground'

export function AddHandoffRuleForm({ base }: { base: OrderWorkflowDefinitionJson }) {
  const overlayEscalation = useWorkflowConfigStore((s) => s.overlay.escalation)
  const addKeywordTrigger = useWorkflowConfigStore((s) => s.addKeywordTrigger)

  const [open, setOpen] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [name, setName] = useState('')
  const [id, setId] = useState('')
  const [idTouched, setIdTouched] = useState(false)
  const [kindChoice, setKindChoice] = useState<string>('quote_request')
  const [customKind, setCustomKind] = useState('')
  const [mode, setMode] = useState<'hard' | 'advisory'>('hard')
  const [messageKey, setMessageKey] = useState('')
  const [phrases, setPhrases] = useState<Record<string, string[]>>({})

  const languages = base.languages ?? []
  const effectiveId = idTouched ? id : deriveKey(name)
  const existingIds = new Set([
    ...(base.escalation?.triggers ?? [])
      .map((t) => t.id)
      .filter((x): x is string => typeof x === 'string'),
    ...Object.keys(overlayEscalation?.triggers ?? {}),
  ])
  const idTaken = existingIds.has(effectiveId)
  const idInvalid = effectiveId !== '' && !KEY_RE.test(effectiveId)
  const advancedOpen = showAdvanced || idTaken || idInvalid
  const kindOptions = RULE_KINDS.filter(
    (k) => k.value !== 'custom' || advancedOpen || kindChoice === 'custom',
  )
  const kind = kindChoice === 'custom' ? customKind.trim() : kindChoice
  // Phrases are atomic chips (SA-125) — no comma parsing anywhere on
  // this tab; a phrase may legitimately contain commas.
  const parsedPhrases = (lang: string) => phrases[lang] ?? []
  const phrasesReady = languages.some((lang) => parsedPhrases(lang).length > 0)
  const messageKeys = Array.from(
    new Set([
      ...Object.keys(base.escalation?.messages ?? {}),
      ...Object.keys(overlayEscalation?.messages ?? {}),
    ]),
  )
  const canSubmit =
    effectiveId !== '' && !idTaken && !idInvalid && kind !== '' && phrasesReady

  const reset = () => {
    setOpen(false); setShowAdvanced(false); setName(''); setId(''); setIdTouched(false)
    setKindChoice('quote_request'); setCustomKind(''); setMode('hard')
    setMessageKey(''); setPhrases({})
  }

  const submit = () => {
    // Build the spec by OMISSION — never write undefined into the overlay.
    const match: Record<string, string[]> = {}
    for (const lang of languages) {
      const list = parsedPhrases(lang)
      if (list.length > 0) match[lang] = list
    }
    const spec: Record<string, unknown> = { kind, when: 'topic_denylist', mode, match }
    if (messageKey) spec.message = messageKey
    addKeywordTrigger(effectiveId, spec)
    reset()
  }

  if (!open) {
    return (
      <button
        type="button"
        className="rounded-xl border border-dashed border-border text-muted-foreground px-3 py-1.5 text-xs font-semibold transition hover:border-primary hover:bg-primary/10 hover:text-primary"
        onClick={() => setOpen(true)}
      >
        + Add a handoff rule
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-border p-3 space-y-2 text-xs text-foreground">
      <p className="font-medium text-foreground">Add a handoff rule</p>
      <p className="text-muted-foreground">
        Fires when the customer mentions any of the phrases below.
      </p>
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-1.5">
          Name
          <input aria-label="name" className={`${inputCls} w-44`} value={name}
            onChange={(e) => setName(e.target.value)} />
        </label>
        {advancedOpen && (
          <label className="flex items-center gap-1.5">
            Id
            <input aria-label="id" className={`${inputCls} w-36 font-mono`} value={effectiveId}
              onChange={(e) => { setId(e.target.value); setIdTouched(true) }} />
          </label>
        )}
        <label className="flex items-center gap-1.5">
          Reason for handoff
          <select aria-label="reason for handoff" className={inputCls} value={kindChoice}
            onChange={(e) => setKindChoice(e.target.value)}>
            {kindOptions.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          aria-expanded={advancedOpen}
          className="text-xs text-muted-foreground underline decoration-dotted hover:text-foreground"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          Advanced
        </button>
        {kindChoice === 'custom' && (
          <label className="flex items-center gap-1.5">
            Custom kind
            <input aria-label="custom kind" className={`${inputCls} w-36 font-mono`}
              value={customKind} onChange={(e) => setCustomKind(e.target.value)} />
          </label>
        )}
      </div>
      {idTaken && <p className="text-destructive">That rule id already exists in this workflow.</p>}
      {idInvalid && <p className="text-destructive">Ids are lowercase letters, digits and underscores, starting with a letter.</p>}

      <div className="space-y-1">
        {languages.map((lang) => (
          <div key={lang} className="max-w-md">
            <span className="font-mono uppercase text-muted-foreground">{lang}</span>{' '}
            phrases
            <TagInput
              value={phrases[lang] ?? []}
              onChange={(list) => setPhrases((p) => ({ ...p, [lang]: list }))}
              normalize={(s) => s.trim()}
              maxTags={50}
              ariaLabel={`phrases in ${lang}`}
              placeholder="Add a phrase and press Enter"
              className="mt-1"
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-1.5">
          Response
          <select aria-label="response" className={inputCls} value={mode}
            onChange={(e) => setMode(e.target.value as 'hard' | 'advisory')}>
            <option value="hard">Hands off immediately</option>
            <option value="advisory">Suggests offering a handoff</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          Handoff message
          <select aria-label="handoff message" className={inputCls} value={messageKey}
            onChange={(e) => setMessageKey(e.target.value)}>
            <option value="">Default</option>
            {messageKeys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button"
          className="rounded-sm px-3 py-1.5 font-medium text-foreground hover:bg-muted"
          onClick={reset}>
          Cancel
        </button>
        <button type="button" disabled={!canSubmit}
          className="rounded-sm bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          onClick={submit}>
          Add
        </button>
      </div>
    </div>
  )
}
